import { GridApi, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { Dictionary, isDefinedNotEmpty } from '@softline/core';
import { Column, isServerSideColumn, ValueColumn } from '../types/grid';
import { IQueryFilter, Query, QueryBuilder, QueryFilter } from '../../query/query.builder';

export function agRequestToQuery(columns: Column<any>[],
                                 api: GridApi,
                                 request: IServerSideGetRowsRequest,
                                 externalFilters: Dictionary<QueryFilter>): Query {
  if(request.groupKeys.length !== request.rowGroupCols.length)
    return toGroupRequest(columns, request, externalFilters);
  else
    return toFlatRequest(columns, request, externalFilters);
}

export function toGroupRequest(columns: Column<any>[], request: IServerSideGetRowsRequest, externalFilters: Dictionary<QueryFilter>): Query {
  const builder = new QueryBuilder();
  builder.start(request.startRow ?? 0);
  builder.end(request.endRow ?? 100);

  const flatColumns = getFlatColumns(columns);
  addColumns(builder, flatColumns)

  for (const [ name, filter ] of Object.entries(externalFilters)) {
    if(isDefinedNotEmpty(filter))
      builder.filter(name, filter);
  }

  const groupedColumns: string[] = [];
  if(request.rowGroupCols) {
    request.rowGroupCols.forEach((c, index) => {
      if(index <= request.groupKeys?.length) {
        const col = flatColumns.find(o => o.colId === c.id);
        if(isServerSideColumn(col)) {
          const value = request.groupKeys && request.groupKeys.length > index ? request.groupKeys[index] : undefined;
          builder.group(col.serverSideAlias ?? col.serverSideField, value);
          groupedColumns.push(col.colId);
        }
      }
    });
  }

  const valueColumns: string[] = [];
  if(request.valueCols) {
    request.valueCols.forEach(v => {
      const col = flatColumns.find(o => o.colId === v.id);
      if(v.aggFunc && isServerSideColumn(col)) {
        builder.value(col.serverSideAlias ?? col.serverSideField, v.aggFunc);
        valueColumns.push(col.colId);
      }
    });
  }
  if(request.pivotCols) {
    request.pivotCols.forEach(c => {
      const col = flatColumns.find(o => o.colId === c.id);
      if(isServerSideColumn(col))
        builder.pivot(col.serverSideAlias ?? col.serverSideField);
    });
  }
  if(request.filterModel) {
    Object.keys(request.filterModel).forEach(colId => {
      const col = flatColumns.find(o => o.colId === colId);
      const filter = request.filterModel![colId];
      if(filter && isServerSideColumn(col)) {
        if(col.type === 'entity') {
          builder.filter(`${col.serverSideField}.id`, filter)
        }
        else {
          builder.filter(col.serverSideField, filter)
        }
      }
    });
  }

  if(request.sortModel) {
    for(const s of request.sortModel) {
      if(!isDefinedNotEmpty(request.rowGroupCols) && flatColumns.findIndex(c => c.colId === s.colId) === -1)
        continue;
      if(isDefinedNotEmpty(request.rowGroupCols)
        && groupedColumns.indexOf(s.colId) === -1
        && valueColumns.indexOf(s.colId) === -1)
        continue;
      const col = flatColumns.find(o => o.colId === s.colId);
      if(isServerSideColumn(col)) {
        const sortField = col.serverSideSortField
          ? `${col.serverSideAlias ?? col.serverSideField}.${col.serverSideSortField}`
          : (col.serverSideAlias ?? col.serverSideField);
        builder.sort(sortField, s.sort as 'asc' | 'desc');
      }
    }
  }
  return builder.build();
}

function toFlatRequest(columns: Column<any>[], request: IServerSideGetRowsRequest, externalFilters: Dictionary<QueryFilter>) {
  const builder = new QueryBuilder();
  builder.start(request.startRow ?? 0);
  builder.end(request.endRow ?? 100);

  const flatColumns = getFlatColumns(columns);
  addColumns(builder, flatColumns);

  for (const [ name, filter ] of Object.entries(externalFilters)) {
    if(isDefinedNotEmpty(filter))
      builder.filter(name, filter);
  }

  //Apply grouping as filters to support expanded groups in flat mode
  let index = 0;
  for (const c of request.rowGroupCols) {
    const col = flatColumns.find(o => o.colId === c.id);
    const filter = request.groupKeys[index] as any;
    if(filter && isServerSideColumn(col)) {
      switch (typeof filter) {
        case 'string':
          builder.filter(col.serverSideAlias ?? col.serverSideField, { type: 'equals', filterType: 'text', filter });
          break;
        case 'number':
          builder.filter(col.serverSideAlias ?? col.serverSideField, { type: 'equals', filterType: 'number', filter, filterTo: null });
          break
        case 'object':
          if((typeof filter.id) === 'number') {
            builder.filter(col.serverSideAlias ?? col.serverSideField+'.id', { type: 'equals', filterType: 'number', filter: filter.id, filterTo: null });
          }
          break
      }
    }
    index++;
  }

  if(request.filterModel) {
    Object.keys(request.filterModel).forEach(colId => {
      const col = flatColumns.find(o => o.colId === colId);
      const filter = request.filterModel![colId];
      if(filter && isServerSideColumn(col)) {
        if(col.type === 'entity') {
          builder.filter(`${col.serverSideField}.id`, filter)
        }
        else {
          builder.filter(col.serverSideField, filter)
        }
      }
    });
  }

  if(request.sortModel) {
    for(const s of request.sortModel) {
      if(!isDefinedNotEmpty(request.rowGroupCols) && flatColumns.findIndex(c => c.colId === s.colId) === -1)
        continue;
      const col = flatColumns.find(o => o.colId === s.colId);
      if(isServerSideColumn(col)) {
        const sortField = col.serverSideSortField
          ? `${col.serverSideAlias ?? col.serverSideField}.${col.serverSideSortField}`
          : (col.serverSideAlias ?? col.serverSideField);
        builder.sort(sortField, s.sort as 'asc' | 'desc');
      }
    }
  }

  return builder.build();
}


function getFlatColumns(columns: Column<any>[]) {
  const flatColumns: (ValueColumn<any>)[] = [];
  const traverse = [...columns.map(o => ({...o, _parentField: null as string | null}))];
  while (traverse.length > 0) {
    const col = traverse.shift();
    if(!col)
      continue;
    if(col.type === 'group')
      traverse.push(...col.children.map(o => ({...o, _parentField: null})));
    else if(col.type === 'entity') {
      if(isServerSideColumn(col)) {
        // TODO: Remove next line when nested entity columns are supported
        flatColumns.push({ ...col });
        traverse.push(...col.children.map(o => ({...o, _parentField: col.serverSideField})));
      }
      else
        traverse.push(...col.children.map(o => ({...o, _parentField: null})));
    }
    else if(isServerSideColumn(col)) {
      flatColumns.push({...col});
    }
  }
  return flatColumns;
}

function addColumns(builder: QueryBuilder, columns: ValueColumn<any>[]) {
  for (const col of columns) {
    if(isServerSideColumn(col))
      builder.column(col.serverSideAlias ?? col.serverSideField, col.serverSideField);
  }
}
